import { Table, Tag, Card, Spin, Alert, Typography, Tooltip, Switch, Button, Space } from 'antd';
import { useEffect, useState } from "react";
import React from "react";
import axios from 'axios';
import {
    TeamOutlined,
    IdcardOutlined,
    SafetyCertificateOutlined,
    ShopOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    TableOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import styles from './NewRules.module.css';
import {instance} from "../../API/API"; // Импорт CSS модуля

const { Title, Text } = Typography;

export const NewRules = () => {
    const [loading, setLoading] = useState(true);
    const [rules, setRules] = useState([]);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('table'); // 'table' или 'card'

    const RoleName = ({ name, description }) => {
        return (
            <div className={styles.roleNameContainer}>
                <div className={styles.roleName}>{name}</div>
                {description && (
                    <div className={styles.roleDescription}>
                        <Tooltip title={description}>
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    };

    const AccessIndicator = ({ hasAccess, label }) => {
        return (
            <div className={styles.accessIndicator}>
                <Tag
                    color={hasAccess ? "green" : "red"}
                    className={styles.accessTag}
                >
                    {hasAccess ? "Доступ есть" : "Нет доступа"}
                </Tag>
                <div className={styles.accessLabel}>{label}</div>
            </div>
        );
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
            responsive: ['md'],
        },
        {
            title: 'Роль',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => <RoleName name={text} description={record.desc} />
        },
        {
            title: 'Сотрудники',
            dataIndex: 'staff',
            key: 'staff',
            align: 'center',
            responsive: ['md'],
            render: (hasAccess) => <AccessIndicator hasAccess={hasAccess} label="Список сотрудников" />
        },
        {
            title: 'Карточки',
            dataIndex: 'card',
            key: 'card',
            align: 'center',
            responsive: ['md'],
            render: (hasAccess) => <AccessIndicator hasAccess={hasAccess} label="Карточки сотрудников" />
        },
        {
            title: 'Роли',
            dataIndex: 'role',
            key: 'role',
            align: 'center',
            responsive: ['md'],
            render: (hasAccess) => <AccessIndicator hasAccess={hasAccess} label="Роли в системе" />
        },
        {
            title: 'Склад',
            dataIndex: 'store',
            key: 'store',
            align: 'center',
            responsive: ['md'],
            render: (hasAccess) => <AccessIndicator hasAccess={hasAccess} label="Склад" />
        },
    ];

    const getRulesData = async () => {
        setLoading(true);
        setError(null);
        try {
             const rules_req = await instance.get(`staff/get_rules.php?`);
            if (rules_req.data && rules_req.data.rules) {
                setRules(rules_req.data.rules.map((el, index) => {
                    return {
                        key: index,
                        id: el.id,
                        name: el.long_name,
                        desc: el.description,
                        staff: Number(el.stafflist_read) === 1,
                        card: Number(el.personalData_read) === 1,
                        role: Number(el.role_read) === 1,
                        store: Number(el.store_read) === 1
                    }
                }));
            }
        } catch (e) {
            console.error("Error fetching rules data:", e);
            setError("Ошибка при загрузке данных. Проверьте подключение к интернету.");
        } finally {
            setLoading(false);
        }
    };

    const RuleCard = ({ rule }) => (
        <Card className={styles.ruleCard} size="small">
            <div className={styles.cardHeader}>
                <Text strong className={styles.ruleId}>#{rule.id}</Text>
                <Text className={styles.ruleName}>{rule.name}</Text>
            </div>

            {rule.desc && (
                <div className={styles.ruleDescription}>
                    <Text type="secondary">{rule.desc}</Text>
                </div>
            )}

            <div className={styles.accessGrid}>
                <div className={styles.accessItem}>
                    <TeamOutlined className={styles.accessIcon} />
                    <AccessIndicator hasAccess={rule.staff} label="Сотрудники" />
                </div>

                <div className={styles.accessItem}>
                    <IdcardOutlined className={styles.accessIcon} />
                    <AccessIndicator hasAccess={rule.card} label="Карточки" />
                </div>

                <div className={styles.accessItem}>
                    <SafetyCertificateOutlined className={styles.accessIcon} />
                    <AccessIndicator hasAccess={rule.role} label="Роли" />
                </div>

                <div className={styles.accessItem}>
                    <ShopOutlined className={styles.accessIcon} />
                    <AccessIndicator hasAccess={rule.store} label="Склад" />
                </div>
            </div>
        </Card>
    );

    useEffect(() => {
        getRulesData();
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Title level={2} className={styles.title}>Управление правами доступа</Title>

                <Space>
                    <Tooltip title="Табличный вид">
                        <Button
                            icon={<TableOutlined />}
                            type={viewMode === 'table' ? 'primary' : 'default'}
                            onClick={() => setViewMode('table')}
                        />
                    </Tooltip>

                    <Tooltip title="Плиточный вид">
                        <Button
                            icon={<AppstoreOutlined />}
                            type={viewMode === 'card' ? 'primary' : 'default'}
                            onClick={() => setViewMode('card')}
                        />
                    </Tooltip>

                    <Tooltip title="Обновить данные">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={getRulesData}
                            loading={loading}
                        />
                    </Tooltip>
                </Space>
            </div>

            {error && (
                <Alert
                    message="Ошибка"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    className={styles.alert}
                />
            )}

            {loading ? (
                <div className={styles.loadingContainer}>
                    <Spin size="large" />
                    <Text>Загрузка прав доступа...</Text>
                </div>
            ) : viewMode === 'table' ? (
                <Table
                    className={styles.table}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        responsive: true
                    }}
                    columns={columns}
                    dataSource={rules}
                    scroll={{ x: true }}
                    size="middle"
                />
            ) : (
                <div className={styles.cardsContainer}>
                    {rules.map(rule => (
                        <RuleCard key={rule.key} rule={rule} />
                    ))}
                </div>
            )}
        </div>
    );
};